import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlusCircle } from 'react-icons/fa';
import './ManageCentres.css';
import API_URL from '../../api/ExamCalendarApi';

const ManageCentres = () => {
    const [centres, setCentres] = useState([]);
    const [stateName, setStateName] = useState('');
    const [districtName, setDistrictName] = useState('');
    const [cityName, setCityName] = useState('');
    const [centreData, setCentreData] = useState([{ name: '' }]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Fetch centres when the component mounts
    useEffect(() => {
        fetchCentres();
    }, []);

    // Fetch all centres
    const fetchCentres = async () => {
        try {
            const response = await axios.get(`${API_URL}/centre/centres`);
            setCentres(response.data.states);
        } catch (error) {
            console.error('Error fetching centres:', error);
        }
    };

    // Handle form input changes for centres
    const handleCentreChange = (index, value) => {
        const updatedCentres = [...centreData];
        updatedCentres[index].name = value;
        setCentreData(updatedCentres);
    };

    // Handle adding a new centre entry form
    const addCentreField = () => {
        setCentreData([...centreData, { name: '' }]);
    };

    // Handle deleting a specific centre entry
    const handleDeleteCentre = (index) => {
        const updatedCentres = centreData.filter((_, i) => i !== index);
        setCentreData(updatedCentres);
    };

    const handleDelete = async (stateName, districtName, cityName, centreName) => {
        try {
            await axios.delete(`${API_URL}/centre/deleteCentre`, { data: { stateName, districtName, cityName, name: centreName } });
            setSuccessMessage('Centre deleted successfully');
            fetchCentres();
        } catch (error) {
            setError('Error deleting centre');
        }
    };

    // Handle form submission for adding a centre
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stateName || !districtName || !cityName || !centreData.some(c => c.name)) {
            setError('State, district, city, and all centre fields are required');
            return;
        }

        const centrePayload = {
            stateName,
            districtName,
            cityName,
            centres: centreData.filter(c => c.name)
        };

        try {
            await axios.post(`${API_URL}/centre/add-centre`, centrePayload);
            setSuccessMessage('Centres added successfully');

            // Reset form and update the centres list
            setStateName('');
            setDistrictName('');
            setCityName('');
            setCentreData([{ name: '' }]);
            fetchCentres();
        } catch (error) {
            setError('Error saving centres');
            setSuccessMessage('');
        }
    };

    return (
        <div className="admin-managecenters-container">
            <h2 className="admin-managecenters-heading">Manage Centres</h2>

            {error && <div className="admin-managecenters-alert admin-managecenters-alert-danger">{error}</div>}
            {successMessage && <div className="admin-managecenters-alert admin-managecenters-alert-success">{successMessage}</div>}

            <form onSubmit={handleSubmit} className="admin-managecenters-form">
                <div className="admin-managecenters-form-group">
                    <label htmlFor="stateName" className="admin-managecenters-form-label">State Name</label>
                    <input
                        type="text"
                        value={stateName}
                        onChange={(e) => setStateName(e.target.value)}
                        className="admin-managecenters-form-control"
                        placeholder="Enter state name"
                    />
                </div>

                <div className="admin-managecenters-form-group">
                    <label htmlFor="districtName" className="admin-managecenters-form-label">District Name</label>
                    <input
                        type="text"
                        value={districtName}
                        onChange={(e) => setDistrictName(e.target.value)}
                        className="admin-managecenters-form-control"
                        placeholder="Enter district name"
                    />
                </div>

                <div className="admin-managecenters-form-group">
                    <label htmlFor="cityName" className="admin-managecenters-form-label">City Name</label>
                    <input
                        type="text"
                        value={cityName}
                        onChange={(e) => setCityName(e.target.value)}
                        className="admin-managecenters-form-control"
                        placeholder="Enter city name"
                    />
                </div>

                <h4 className="admin-managecenters-heading">Centres</h4>
                {centreData.map((centre, index) => (
                    <div key={index} className="admin-managecenters-centre-entry">
                        <div className="admin-managecenters-form-group">
                            <label htmlFor={`centreName-${index}`} className="admin-managecenters-form-label">Centre Name</label>
                            <input
                                type="text"
                                value={centre.name}
                                onChange={(e) => handleCentreChange(index, e.target.value)}
                                className="admin-managecenters-form-control"
                                placeholder="Enter centre name"
                            />
                        </div>

                        {/* Delete button for each centre, only show if centre has been added */}
                        {centreData.length > 1 && (
                            <button
                                type="button"
                                onClick={() => handleDeleteCentre(index)}
                                className="admin-managecenters-btn-delete"
                            >
                                Delete Centre
                            </button>
                        )}
                    </div>
                ))}

                <div className="admin-managecenters-actions">
                    <button
                        type="button"
                        onClick={addCentreField}
                        className="admin-managecenters-btn-add"
                    >
                        <FaPlusCircle className="admin-managecenters-icon" /> Add one more centre details
                    </button>

                    <button type="submit" className="admin-managecenters-btn-submit">
                        Add Centres
                    </button>
                </div>
            </form>

            <h3 className="admin-managecenters-heading">Centres List</h3>
            <div className="admin-managecenters-table-wrapper">
            <table className="admin-managecenters-table">
                <thead>
                    <tr>
                        <th>State</th>
                        <th>District</th>
                        <th>City</th>
                        <th>Centre Name</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {centres.map((state) =>
                        state.districts.map((district) =>
                            district.cities.map((city) =>
                                city.centres.map((centre) => (
                                    <tr key={centre.name}>
                                        <td>{state.stateName}</td>
                                        <td>{district.districtName}</td>
                                        <td>{city.cityName}</td>
                                        <td>{centre.name}</td>
                                        <td>
                                            <button
                                                onClick={() => handleDelete(state.stateName, district.districtName, city.cityName, centre.name)}
                                                className="admin-managecenters-btn-delete"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )
                        )
                    )}
                </tbody>
            </table>
            </div>
        </div>
    );
};

export default ManageCentres;
